import React, { useEffect, useState } from 'react';
import { fetchCommitments, createCommitment, updateCommitment, deleteCommitment, filterCommitments } from './api';
import CustomNavbar from '../CustomPermissions/CustomNavbar';

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

const CommitmentList = () => {
    const [commitments, setCommitments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCommitmentId, setExpandedCommitmentId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [users, setAllUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(localStorage.getItem('user_id') || '');
    const [isSuperuser, setSuperuser] = useState(localStorage.getItem('is_superuser') || '');
    const [userPercentages, setUserPercentages] = useState({});
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [currentCommitment, setCurrentCommitment] = useState({
        commitment_description: '',
        commitment_in_number: null,
        evening_commitment_in_number: '',
        number_of_calls: '',
        one_thirty_min: '',
        note: '',
        commitment_status: 'Pending',
    });
    const [filters, setFilters] = useState({
        status: '',
        name: '',
        req_date: ''
    });

    // Helper function to check if a commitment was created today
    const isCommitmentFromToday = (createdAt) => {
        const today = new Date();
        const commitmentDate = new Date(createdAt);

        return (
            today.getFullYear() === commitmentDate.getFullYear() &&
            today.getMonth() === commitmentDate.getMonth() &&
            today.getDate() === commitmentDate.getDate()
        );
    };

    // Fetch commitments
    useEffect(() => {
        const getCommitments = async () => {
            try {
                setIsLoading(true);
                const response = await fetchCommitments();
                const sortedData = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setCommitments(sortedData);
                setError(null);
            } catch (error) {
                console.error('Error fetching commitments:', error);
                setError('Failed to load commitments. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        getCommitments();
    }, []);

    // Fetch all users
    useEffect(() => {
        fetch(getApiUrl("/Authentication/all_users_list/"), {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        }) // Fetch user data from Django API
            .then((res) => res.json())
            .then((data) => setAllUsers(Array.isArray(data) ? data : []))
            .catch((error) => console.error("Error fetching users:", error));
    }, []);

    // Fetch user percentages
    useEffect(() => {
        const fetchUserPercentages = async () => {
            try {
                const response = await fetch(getApiUrl("/Commitment/commitments/percentage/"), {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                });
                const data = await response.json();
                // Convert the array of user percentages into an object for quick lookup
                const percentages = {};
                data.forEach(user => {
                    percentages[user.userid] = {
                        taskCompletionPercentage: user.task_completion_percentage,
                        totalCommitmentPercentage: user.total_commitment_percentage,
                    };
                });
                setUserPercentages(percentages);
            } catch (error) {
                console.error("Error fetching user percentages:", error);
            }
        };
        fetchUserPercentages();
    }, []);


    // Handle delete commitment
    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this commitment?');
        if (confirmDelete) {
            try {
                await deleteCommitment(id);
                setCommitments(commitments.filter((c) => c.id !== id));
                setExpandedCommitmentId(null);
            } catch (error) {
                console.error('Error deleting commitment:', error);
                alert('Failed to delete commitment. Please try again.');
            }
        }
    };

    // Open modal for creating/editing a commitment
    const openModal = (mode, commitment = null) => {
        setModalMode(mode);
        setCurrentCommitment(mode === 'create'
            ? {
                commitment_description: '',
                commitment_in_number: null,
                evening_commitment_in_number: '',
                number_of_calls: '',
                one_thirty_min: '',
                note: '',
                commitment_status: 'Pending',
            }
            : { ...commitment }
        );
        setIsModalOpen(true);
    };

    // Handle modal submission
    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            // Clean payload: Convert empty strings to null for integer fields
            const cleanedCommitment = { ...currentCommitment };
            if (cleanedCommitment.evening_commitment_in_number === '') {
                cleanedCommitment.evening_commitment_in_number = null;
            }
            if (cleanedCommitment.one_thirty_min === '') {
                cleanedCommitment.one_thirty_min = null;
            }
            if (cleanedCommitment.number_of_calls === '') {
                cleanedCommitment.number_of_calls = null;
            }

            if (modalMode === 'create') {
                const commitmentData = {
                    ...cleanedCommitment,
                    user: currentUser // Using the username from state
                };
                const response = await createCommitment(commitmentData);
                // Add new commitment at the beginning (most recent first)
                setCommitments([response.data, ...commitments]);
            } else {
                const response = await updateCommitment(cleanedCommitment.id, cleanedCommitment);
                setCommitments(commitments.map(c =>
                    c.id === cleanedCommitment.id ? response.data : c
                ));
                setExpandedCommitmentId(null);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} commitment:`, error);
            alert(`Failed to ${modalMode === 'create' ? 'create' : 'update'} commitment. Please try again.`);
        }
    };

    // Handle input changes in the modal
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentCommitment(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Toggle expanded row for additional details
    const toggleExpandedRow = (id) => {
        setExpandedCommitmentId(expandedCommitmentId === id ? null : id);
    };

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Apply filters
    const handleFilter = async () => {
        try {
            setIsLoading(true);
            const response = await filterCommitments(filters);
            const sortedResults = response.data.results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setCommitments(sortedResults);
            setError(null);
        } catch (error) {
            console.error('Error filtering commitments:', error);
            setError('Failed to filter commitments. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Reset filters
    const resetFilters = async () => {
        setFilters({
            status: '',
            name: '',
            req_date: ''
        });
        try {
            setIsLoading(true);
            const response = await fetchCommitments();
            const sortedData = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setCommitments(sortedData);
            setError(null);
        } catch (error) {
            console.error('Error resetting filters:', error);
            setError('Failed to reset filters. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <p className="text-gray-600 text-lg">Loading commitments...</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="text-red-700 font-semibold mb-2">Error</h3>
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
            <CustomNavbar />
            <div className="container mx-auto px-4 py-4">
                {/* Commitment Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                                    {modalMode === 'create' ? 'Create New Commitment' : 'Edit Commitment'}
                                </h2>
                            </div>
                            <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                                <form onSubmit={handleModalSubmit} className="space-y-4">
                                    <div>
                                        <label
                                            htmlFor="commitment_in_number"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            Commitment in Number
                                        </label>
                                        <input
                                            type="number"
                                            id="commitment_in_number"
                                            name="commitment_in_number"
                                            value={currentCommitment.commitment_in_number}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="commitment_in_number"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            Evening Commitment
                                        </label>
                                        <input
                                            type="number"
                                            id="evening_commitment_in_number"
                                            name="evening_commitment_in_number"
                                            value={currentCommitment.evening_commitment_in_number}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="number_of_calls"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            Connected Calls
                                        </label>
                                        <input
                                            type="number"
                                            id="number_of_calls"
                                            name="number_of_calls"
                                            value={currentCommitment.number_of_calls}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="one_thirty_min"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            1:30 min
                                        </label>
                                        <input
                                            type="number"
                                            id="one_thirty_min"
                                            name="one_thirty_min"
                                            value={currentCommitment.one_thirty_min}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="commitment_description"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            Description
                                        </label>
                                        <textarea
                                            id="commitment_description"
                                            name="commitment_description"
                                            value={currentCommitment.commitment_description}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="note"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            Additional Notes
                                        </label>
                                        <textarea
                                            id="note"
                                            name="note"
                                            value={currentCommitment.note}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex flex-col space-y-1 pt-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <span className="text-xs text-gray-500 mb-2">(Auto 'Pending' if Evening Commitment is blank)</span>
                                        <div className="flex bg-gray-100 p-1 rounded-lg">
                                            <button
                                                type="button"
                                                onClick={() => handleInputChange({ target: { name: 'commitment_status', value: 'Completed' } })}
                                                className={`flex-1 flex items-center justify-center py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${currentCommitment.commitment_status === 'Completed'
                                                    ? 'bg-white text-green-700 shadow-sm border border-gray-200'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                                                    }`}
                                            >
                                                {currentCommitment.commitment_status === 'Completed' && (
                                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                                )}
                                                Completed
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleInputChange({ target: { name: 'commitment_status', value: 'Not Completed' } })}
                                                className={`flex-1 flex items-center justify-center py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${currentCommitment.commitment_status === 'Not Completed'
                                                    ? 'bg-white text-red-700 shadow-sm border border-gray-200'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                                                    }`}
                                            >
                                                {currentCommitment.commitment_status === 'Not Completed' && (
                                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                                )}
                                                Not Completed
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-4 mt-6 pt-4 border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            {modalMode === 'create' ? 'Create' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-3xl font-bold text-gray-800">My Commitments</h2>
                    <button
                        onClick={() => openModal('create')}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out"
                    >
                        + Create Commitment
                    </button>
                </div>

                {/* Filter Section */}
                {isSuperuser === 'true' && (
                    <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-700">Filter Commitments</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                    Name contains
                                </label>
                                <select
                                    id="name"
                                    className="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                                    name='name'
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Select a User</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.id} - {user.username}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Not Completed">Not Completed</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="req_date" className="block text-sm font-medium text-gray-700 mb-1">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    id="req_date"
                                    name="req_date"
                                    value={filters.req_date}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex items-end space-x-2">
                                <button
                                    onClick={handleFilter}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Apply Filters
                                </button>
                                <button
                                    onClick={resetFilters}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Commitments Table */}
                {commitments.length === 0 ? (
                    <div className="bg-gray-100 border border-gray-200 p-6 rounded-lg text-center">
                        <p className="text-gray-600">No commitments found. Create your first commitment!</p>
                    </div>
                ) : (
                    <div className="bg-white shadow-md rounded-lg">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-blue-500 border-b">
                                    <th className="py-3 px-4 text-center text-white font-semibold">User</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Date</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Morning Commitment</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Connected Calls</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">1:30 min</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Evening Report</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Status</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Task %</th>
                                    <th className="py-3 px-4 text-right text-white font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commitments.map((commitment) => (
                                    <React.Fragment key={commitment.id}>
                                        <tr className="border-b last:border-b-0 hover:bg-gray-50 transition duration-200">
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full font-semibold`}>
                                                    {commitment.user_username}
                                                </span>
                                            </td>
                                            <td
                                                className="py-3 px-4 cursor-pointer"
                                                onClick={() => toggleExpandedRow(commitment.id)}
                                            >
                                                <div className="flex items-center">
                                                    <span className="mr-2">
                                                        {expandedCommitmentId === commitment.id ? '▼' : '▶'}
                                                    </span>
                                                    <span className="font-medium">
                                                        {new Intl.DateTimeFormat('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        }).format(new Date(commitment.created_at))}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-center font-semibold`}>
                                                    {commitment.commitment_in_number}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-center font-semibold`}>
                                                    {commitment.number_of_calls}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-center font-semibold`}>
                                                    {commitment.one_thirty_min}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-center font-semibold`}>
                                                    {commitment.evening_commitment_in_number}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {(() => {
                                                    // Normalize legacy boolean true/false to display labels
                                                    const raw = commitment.commitment_status;
                                                    const label =
                                                        raw === true || raw === 'true'
                                                            ? 'Completed'
                                                            : raw === false || raw === 'false'
                                                                ? 'Not Completed'
                                                                : raw; // already 'Completed', 'Not Completed', 'Pending', etc.
                                                    const colorClass =
                                                        label === 'Completed'
                                                            ? 'bg-green-100 text-green-800'
                                                            : label === 'Not Completed'
                                                                ? 'bg-red-100 text-red-800'
                                                                : 'bg-yellow-100 text-yellow-800';
                                                    return (
                                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
                                                            {label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {commitment.completion_percentage} %
                                            </td>

                                            <td className="py-3 px-4 text-right space-x-2">
                                                {isCommitmentFromToday(commitment.created_at) && (commitment.user == currentUser || isSuperuser === 'true') && (
                                                    <button
                                                        onClick={() => openModal('edit', commitment)}
                                                        className="text-blue-500 hover:text-blue-700 font-medium mr-2"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {isCommitmentFromToday(commitment.created_at) && (commitment.user == currentUser || isSuperuser === 'true') && (
                                                    <button
                                                        onClick={() => handleDelete(commitment.id)}
                                                        className="text-red-500 hover:text-red-700 font-medium"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {expandedCommitmentId === commitment.id && (
                                            <tr>
                                                <td colSpan="7" className="p-4 bg-gray-50">
                                                    <div className="space-y-2">
                                                        {commitment.commitment_description && (
                                                            <div>
                                                                <h4 className="font-semibold text-gray-700">Description:</h4>
                                                                <p className="text-gray-600">
                                                                    {commitment.commitment_description}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {commitment.commitment_in_number !== null && (
                                                            <div>
                                                                <h4 className="font-semibold text-gray-700">Commitment in Number:</h4>
                                                                <p className="text-gray-600">
                                                                    {commitment.commitment_in_number}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {commitment.number_of_calls !== null && (
                                                            <div>
                                                                <h4 className="font-semibold text-gray-700">Connected Calls:</h4>
                                                                <p className="text-gray-600">
                                                                    {commitment.number_of_calls}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {commitment.note && (
                                                            <div>
                                                                <h4 className="font-semibold text-gray-700">Additional Notes:</h4>
                                                                <p className="text-gray-600">
                                                                    {commitment.note}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {commitment.updated_at && (
                                                            <div>
                                                                <h4 className="font-semibold text-gray-700">Updated At:</h4>
                                                                <p className="text-gray-600">
                                                                    {new Intl.DateTimeFormat('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric',
                                                                        hour: 'numeric',
                                                                        minute: 'numeric',
                                                                        hour12: true,
                                                                    }).format(new Date(commitment.updated_at))}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommitmentList;